import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import DataTable from '../components/common/DataTable';
import LoadingSpinner from '../components/common/LoadingSpinner';
import '../styles/AppointmentsPage.css';
import { getAppointments, getRecurringAppointments } from '../utils/api';
import {
  Calendar,
  Clock,
  Repeat,
  User,
  Briefcase,
  MapPin,
  CheckCircle,
  CalendarDays,
  Activity
} from 'lucide-react';

const AppointmentsPage = () => {
  const [activeTab, setActiveTab] = useState('upcoming');

  const { data: appointments, isLoading } = useApi(getAppointments, { immediate: true });
  const { data: recurring } = useApi(getRecurringAppointments, { immediate: true });
  const now = new Date();
  const todayKey = now.toDateString();
  const todayAppointments = (appointments || []).filter(a => new Date(a.date).toDateString() === todayKey);
  const completedAppointments = (appointments || []).filter(a => a.status === 'completed').length;
  const monthName = now.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });

  const statusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-500/20 text-blue-400',
      confirmed: 'bg-green-500/20 text-green-400',
      in_progress: 'bg-purple-500/20 text-purple-400',
      completed: 'bg-teal-500/20 text-teal-400',
      cancelled: 'bg-red-500/20 text-red-400',
      no_show: 'bg-gray-500/20 text-gray-400',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.scheduled}`}>
        {status === 'scheduled' ? 'Agendado' :
         status === 'confirmed' ? 'Confirmado' :
         status === 'in_progress' ? 'Em Andamento' :
         status === 'completed' ? 'Completado' :
         status === 'cancelled' ? 'Cancelado' :
         status === 'no_show' ? 'Não Compareceu' : status}
      </span>
    );
  };

  const tabs = [
    { id: 'upcoming', label: 'Próximos', count: (appointments || []).filter(a => new Date(a.date) > new Date()).length },
    { id: 'today', label: 'Hoje', count: (appointments || []).filter(a => {
      const d = new Date(a.date);
      const today = new Date();
      return d.toDateString() === today.toDateString();
    }).length },
    { id: 'completed', label: 'Completados', count: (appointments || []).filter(a => a.status === 'completed').length },
    { id: 'recurring', label: 'Recorrentes', count: (recurring || []).length },
    { id: 'all', label: 'Todos', count: (appointments || []).length },
  ];

  const columns = [
    { key: 'id', label: 'ID', width: '60px' },
    { key: 'client_name', label: 'Cliente', render: (row) => (
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-gray-500" />
        <span>{row.client_name}</span>
      </div>
    )},
    { key: 'worker_name', label: 'Profissional', render: (row) => (
      <div className="flex items-center gap-2">
        <Briefcase className="w-4 h-4 text-gray-500" />
        <span>{row.worker_name}</span>
      </div>
    )},
    { key: 'service_type', label: 'Serviço' },
    { key: 'date', label: 'Data', render: (row) => (
      <div className="flex items-center gap-1">
        <Calendar className="w-3.5 h-3.5 text-gray-500" />
        <span>{row.date ? new Date(row.date).toLocaleDateString('pt-PT') : '-'}</span>
      </div>
    )},
    { key: 'time', label: 'Hora', render: (row) => (
      <div className="flex items-center gap-1">
        <Clock className="w-3.5 h-3.5 text-gray-500" />
        <span>{row.time || '-'}</span>
      </div>
    )},
    { key: 'location', label: 'Local', render: (row) => (
      <div className="flex items-center gap-1 text-gray-400">
        <MapPin className="w-3.5 h-3.5" />
        <span className="text-xs truncate max-w-[100px]">{row.location || 'N/A'}</span>
      </div>
    )},
    { key: 'status', label: 'Status', render: (row) => statusBadge(row.status) },
    { key: 'is_recurring', label: 'Recorrente', render: (row) => (
      row.is_recurring ? (
        <span className="flex items-center gap-1 text-teal-400 text-xs">
          <Repeat className="w-3.5 h-3.5" />
          Sim
        </span>
      ) : (
        <span className="text-gray-500 text-xs">Não</span>
      )
    )},
  ];

  const getTabData = () => {
    if (!appointments) return [];
    switch (activeTab) {
      case 'upcoming':
        return appointments.filter(a => new Date(a.date) > new Date() && a.status !== 'completed');
      case 'today': {
        const today = new Date().toDateString();
        return appointments.filter(a => new Date(a.date).toDateString() === today);
      }
      case 'completed':
        return appointments.filter(a => a.status === 'completed');
      case 'recurring':
        return recurring || [];
      default:
        return appointments;
    }
  };

  return (
    <div className="appointments-admin-page space-y-6">
      <section className="appointments-admin-hero">
        <div>
          <span className="appointments-admin-eyebrow"><Activity className="w-4 h-4" /> Operação em tempo real</span>
          <h1>Agenda de serviços</h1>
          <p>Uma visão clara dos compromissos, recorrências e serviços concluídos pela rede Dozy.</p>
        </div>
        <div className="appointments-today-card">
          <CalendarDays className="w-6 h-6" />
          <div><strong>{todayAppointments.length}</strong><span>serviços marcados para hoje</span></div>
        </div>
      </section>

      {/* Calendar Overview */}
      <div className="appointments-overview">
        <section className="appointments-calendar">
          <div className="appointments-calendar__head"><div><span>Calendário</span><h3>{monthName}</h3></div><span className="appointments-live-dot">Agenda ativa</span></div>
          <div className="appointments-calendar__grid">
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
              <div key={day} className="text-center text-gray-500 text-xs font-medium py-2">{day}</div>
            ))}
            {Array.from({ length: 42 }, (_, i) => {
              const date = new Date();
              date.setDate(1);
              const firstDay = date.getDay();
              const dayNum = i - firstDay + 1;
              const currentDate = new Date(date.getFullYear(), date.getMonth(), dayNum);
              const isToday = currentDate.toDateString() === new Date().toDateString();
              const hasAppointment = (appointments || []).some(a => 
                new Date(a.date).toDateString() === currentDate.toDateString()
              );
              const isCurrentMonth = dayNum > 0 && dayNum <= new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

              return (
                <div
                  key={i}
                  className={`appointments-calendar__day ${isToday ? 'is-today' : ''} ${isCurrentMonth ? '' : 'is-outside'} ${hasAppointment ? 'has-appointment' : ''}`}
                >
                  {isCurrentMonth ? dayNum : ''}
                  {hasAppointment && !isToday && (
                    <i />
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <aside className="appointments-summary">
          <article className="appointments-summary-card appointments-summary-card--blue"><div><Calendar /></div><span>Total de marcações<strong>{(appointments || []).length}</strong><small>em toda a agenda</small></span></article>
          <article className="appointments-summary-card appointments-summary-card--cyan"><div><Repeat /></div><span>Planos recorrentes<strong>{(recurring || []).length}</strong><small>serviços programados</small></span></article>
          <article className="appointments-summary-card appointments-summary-card--green"><div><CheckCircle /></div><span>Serviços concluídos<strong>{completedAppointments}</strong><small>total acumulado</small></span></article>
        </aside>
      </div>

      {/* Tabs */}
      <div className="appointments-admin-tabs-shell">
        <div className="appointments-admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? 'is-active' : ''}
            >
              {tab.label}
              <span>{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <section className="appointments-admin-table">
      {isLoading && !appointments ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <DataTable
          data={getTabData()}
          columns={columns}
          pageSize={10}
          emptyMessage="Nenhum agendamento encontrado."
        />
      )}
      </section>
    </div>
  );
};

export default AppointmentsPage;
